import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';
import { lotSchema } from './inventory';

/* Recall cases and lot impact (P1 §5.2, ADR-0028). Quantities: base unit, scale 1000. */

export const lotImpactSchema = z.object({
  lot_id: uuidSchema,
  lot_code: z.string(),
  on_hand_scaled: z.number().int(),
  on_hand_value_minor: z.number().int(),
  consumed_scaled: z.number().int(),
  consumed_order_items: z.number().int(),
  wasted_scaled: z.number().int(),
  counted_adjustment_scaled: z.number().int(),
  transfers: z.array(
    z.object({
      transfer_id: uuidSchema,
      transfer_number: z.string(),
      to_branch_id: uuidSchema,
      /** Empty when the caller may not see that branch. */
      to_branch_name: z.string(),
      qty_scaled: z.number().int(),
      /** Destination lot, when the caller may see that branch. */
      destination_lot_id: uuidSchema.nullable(),
      restricted: z.boolean(),
    }),
  ),
});

export const recallCaseSchema = z.object({
  id: uuidSchema,
  case_number: z.string(),
  title: z.string(),
  status: z.enum(['open', 'closed']),
  item_id: uuidSchema,
  item_name: z.string(),
  supplier_id: uuidSchema.nullable(),
  supplier_name: z.string().nullable(),
  owner_user_id: uuidSchema,
  owner_name: z.string().nullable(),
  reason: z.string(),
  resolution_notes: z.string().nullable(),
  opened_by_name: z.string().nullable(),
  closed_by_name: z.string().nullable(),
  closed_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
  version: z.number().int(),
  lots: z.array(lotSchema),
  impact: z.array(lotImpactSchema),
});
export type RecallCase = z.infer<typeof recallCaseSchema>;
export const recallCaseListResponseSchema = z.object({
  items: z.array(
    recallCaseSchema.omit({ lots: true, impact: true }).extend({ lot_count: z.number().int() }),
  ),
});

export const createRecallCaseRequestSchema = z.object({
  title: z.string().trim().min(3).max(120),
  item_id: uuidSchema,
  lot_ids: z.array(uuidSchema).min(1).max(100),
  supplier_id: uuidSchema.nullable().default(null),
  /** Default: the caller. */
  owner_user_id: uuidSchema.optional(),
  reason: z.string().trim().min(3).max(500),
});
export const closeRecallCaseRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  resolution_notes: z.string().trim().min(3).max(1000),
  /** Release lots still recalled back to available (e.g. supplier withdrew the recall). */
  release_lots: z.boolean().default(false),
});

export const recallSearchQuerySchema = z.object({
  lot_code: z.string().trim().min(1).max(40).optional(),
  supplier_id: uuidSchema.optional(),
  item_id: uuidSchema.optional(),
});
export const recallSearchResponseSchema = z.object({ items: z.array(lotSchema) });
